import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Lightweight inline error banner.
 *
 * The shared `@qrody/ui` ErrorBanner pulls in `next/link` which doesn't
 * resolve under Vite. Until that dependency is cleaned up (sub-iter
 * destructive-cleanup phase), the SPA renders its own copy so a single
 * unresolved import doesn't take the whole bundle down.
 */
export interface BannerError {
  status: number;
  title: string;
  body: string;
  helpLink?: string;
}

export function ErrorAlert({
  error,
  onRetry,
  className,
}: {
  error: BannerError;
  onRetry?: () => void;
  className?: string;
}) {
  const tone = bannerTone(error.status);
  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn("flex gap-3 rounded-md border p-4 text-sm", tone, className)}
    >
      <AlertCircle className="mt-0.5 size-5 shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="flex items-baseline justify-between gap-4">
          <p className="font-semibold leading-tight">{error.title}</p>
          <span className="text-xs opacity-70">
            {error.status > 0 ? `HTTP ${error.status}` : "Offline"}
          </span>
        </div>
        <p className="opacity-80">{error.body}</p>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {error.helpLink ? (
            <a
              href={error.helpLink}
              className="inline-flex items-center text-xs font-medium underline-offset-4 hover:underline"
            >
              Mehr erfahren
            </a>
          ) : null}
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
            >
              Erneut versuchen
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function bannerTone(status: number): string {
  if (status === 0) return "border-amber-300 bg-amber-50 text-amber-900";
  if (status >= 500) return "border-red-300 bg-red-50 text-red-900";
  return "border-orange-300 bg-orange-50 text-orange-900";
}
